import { useEffect, useState } from "react";
import { UserPlus, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RoleBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { api, inr, DEPT, formatErr } from "@/lib/api";

const EMPTY = { full_name: "", email: "", phone_number: "", password: "", role: "staff", department: "service", base_monthly_salary: "", daily_allowance_rate: "" };

const UserForm = ({ initial, onClose, onSaved }) => {
  const { user } = useAuth();
  const editing = !!initial?.id;
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial, password: "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    const body = { ...f, base_monthly_salary: Number(f.base_monthly_salary || 0), daily_allowance_rate: Number(f.daily_allowance_rate || 0) };
    try {
      if (editing) {
        const { full_name, phone_number, role, department, base_monthly_salary, daily_allowance_rate, active, password } = body;
        await api.put(`/users/${initial.id}`, { full_name, phone_number, role, department, base_monthly_salary, daily_allowance_rate, active, password: password || null });
      } else {
        await api.post("/users", body);
      }
      toast.success(editing ? "Staff updated" : "Staff account created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-3" data-testid="staff-form">
      <div><Label>Full Name</Label><Input required value={f.full_name} onChange={set("full_name")} data-testid="staff-name-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Email</Label><Input required type="email" disabled={editing} value={f.email} onChange={set("email")} data-testid="staff-email-input" /></div>
        <div><Label>Phone</Label><Input required value={f.phone_number} onChange={set("phone_number")} data-testid="staff-phone-input" /></div>
      </div>
      <div><Label>{editing ? "New Password (optional)" : "Password"}</Label><Input type="text" required={!editing} minLength={6} value={f.password} onChange={set("password")} data-testid="staff-password-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Role</Label>
          <Select value={f.role} onValueChange={set("role")}>
            <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="staff">Staff</SelectItem><SelectItem value="hr">HR</SelectItem>
              {user.role === "director" && <SelectItem value="director">Director</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Department</Label>
          <Select value={f.department} onValueChange={set("department")}>
            <SelectTrigger data-testid="staff-dept-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{Object.entries(DEPT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Base Monthly Salary (₹)</Label><Input required type="number" min="0" value={f.base_monthly_salary} onChange={set("base_monthly_salary")} data-testid="staff-salary-input" /></div>
        <div><Label>Daily Allowance (₹)</Label><Input type="number" min="0" value={f.daily_allowance_rate} onChange={set("daily_allowance_rate")} data-testid="staff-da-input" /></div>
      </div>
      {editing && (
        <label className="flex items-center justify-between border rounded-lg p-3 text-sm">Account active<Switch checked={f.active !== false} onCheckedChange={set("active")} data-testid="staff-active-switch" /></label>
      )}
      <Button type="submit" disabled={busy} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="staff-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
    </form>
  );
};

export default function StaffDirectory() {
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Staff Directory</h1>
          <p className="text-sm text-slate-500 mt-1">Only HR and the Director can create accounts. There is no public sign-up.</p>
        </div>
        <Button onClick={() => setEdit(null)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-staff-button"><UserPlus className="h-4 w-4 mr-2" />Add Staff</Button>
      </div>
      <div className="bg-white border rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Role</TableHead><TableHead>Department</TableHead>
            <TableHead className="text-right">Base Salary</TableHead><TableHead className="text-right">DA / day</TableHead><TableHead>Status</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`staff-row-${u.id}`} className={u.active === false ? "opacity-50" : ""}>
                <TableCell className="font-semibold">{u.full_name}</TableCell>
                <TableCell className="text-xs text-slate-600">{u.email}<br />{u.phone_number}</TableCell>
                <TableCell><RoleBadge role={u.role} /></TableCell>
                <TableCell>{DEPT[u.department]}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(u.base_monthly_salary)}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(u.daily_allowance_rate)}</TableCell>
                <TableCell className="text-xs">{u.active === false ? "Inactive" : "Active"}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => setEdit(u)} data-testid={`edit-staff-${u.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)}>
        <DialogContent className="bg-white max-w-lg" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{edit ? "Edit Staff" : "New Staff Account"}</DialogTitle></DialogHeader>
          {edit !== undefined && <UserForm initial={edit} onClose={() => setEdit(undefined)} onSaved={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
